import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, Tooltip, useTheme } from '@mui/material';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { useAtom, useSetAtom } from 'jotai';
import useInfiniteQuery from '../../../Hooks/useInfiniteQuery';
import useAPI from '../../../api';
import {
  additionalCustomerDataAtom,
  openSnackbarAdviseAtom,
  searchValuesAtom,
  selectedCustomerIdAtom,
} from '../../../Atoms';
import SectionPage from '../../../Components/Paper/SectionPage';
import CustomDialog from '../../../Components/Dialog/CustomDialog';
import ContentBodyBox from '../../../Components/ContentBodyBox';
import mapFilters from '../../../Helpers/Filters/mapFilters';
import FiltersWidget from './FiltersWidget';
import SendsSummary from './SendsSummary';
import { CONTACT_COLUMNS, FILTERS, SORTING } from '../Utils/constants';
import useAbortEffect from '../../../Hooks/useAbortEffect';
import formatMassiveStats from '../Utils/formatMassiveStats';

const HTML_TAG = /<\/?[a-z][^>]*>/i;

//html messages render as-is; plain text (sms, text emails) is escaped and keeps its line breaks
const toDisplayHtml = (message = '') =>
  HTML_TAG.test(message)
    ? message
    : message
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');

const SendsAnalysisPage = function SendsAnalysisPage({ heightQuery = true }) {
  const API = useAPI();
  const intl = useIntl();
  const theme = useTheme();
  const locationParams = useLocation();
  const {
    campaignName = null,
    branchId = null,
    activityType = null,
  } = locationParams?.state || {};

  const setOpenSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const setSelectedCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);

  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [messageToShow, setMessageToShow] = useState(null);

  const handleOpenCustomer = ({
    entityId,
    branchId: customerBranchId,
    customerName,
    customerSurname,
  }) => {
    setSelectedCustomerId(entityId);
    setAdditionalCustomerData({
      wavePeriod: null,
      branchId: customerBranchId,
      customerName: `${customerName} ${customerSurname}`,
      outcomesFilter: 'CUSTOMERS',
    });
  };

  useEffect(() => {
    if (campaignName)
      setSearchValues((prev) => ({
        ...prev,
        campaignName,
        branchId: branchId ?? null,
        activityType: activityType ?? null,
      }));
  }, [campaignName, branchId, activityType]);

  useEffect(() => {
    API.getAllTags(
      { silent: true },
      (res) => {
        setTags(res);
      },
      () => {
        setOpenSnackbar({
          severity: 'error',
          message: intl.messages.TAGS_LOADING_ERROR,
        });
      }
    );
  }, []);

  const getSendAnalysis = useCallback(
    (pageNumber) =>
      API.getMassiveDetails(
        {
          pageNumber,
          silent: pageNumber > 1,
          filterExpression: mapFilters(
            ['fullNameSearch', 'phoneNumber', 'email'],
            FILTERS,
            [],
            searchValues
          ),
          sortExpression: searchValues?.sortValue
            ? {
                sortValue: searchValues?.sortValue.value,
                sort: searchValues?.sortValue.direction,
              }
            : null,
        },
        () => {}
      ),
    [searchValues]
  );
  const { ObserverWrapper } = useInfiniteQuery(getSendAnalysis);

  const handleApplyFilters = (newFilters) => {
    setSearchValues((prev) => ({ ...prev, ...newFilters }));
  };

  //same scope as the list: clearing filters must widen both
  const {
    campaignName: statsCampaignName = null,
    branchId: statsBranchId = null,
    activityType: statsActivityType = null,
  } = searchValues || {};

  const getMassiveStats = (signal) => {
    setStats(null);
    API.getMassiveActivities(
      {
        campaignName: statsCampaignName,
        branchId: statsBranchId,
        activityType: statsActivityType,
      },
      { silent: true, signal },
      (res) => {
        setStats(formatMassiveStats(res, intl, theme));
      },
      () => {
        //set stats to empty object to show NO DATA message (not when replaced by a newer request)
        if (!signal.aborted) setStats({});
      }
    );
  };

  //stats follow campaign, branch and channel; outcome filters only narrow the list
  useAbortEffect(getMassiveStats, [
    statsCampaignName,
    JSON.stringify(statsBranchId),
    JSON.stringify(statsActivityType),
  ]);

  return (
    <>
      <CustomDialog
        open={Boolean(messageToShow)}
        close={() => setMessageToShow(null)}
        maxWidth="sm"
        fullWidth
        title={`${intl.messages.MESSAGE_CONTENT} - ${messageToShow?.customerName} ${messageToShow?.customerSurname}`}
        noForm
        showOnly
        disableActions
      >
        <ContentBodyBox
          content={toDisplayHtml(messageToShow?.message ?? '')}
          styles={{
            root: { border: 'none' },
            box: { p: 0 },
            content: {
              fontFamily: 'Roboto',
              whiteSpace: HTML_TAG.test(messageToShow?.message ?? '')
                ? 'normal'
                : 'pre-wrap',
            },
          }}
        />
      </CustomDialog>
      <SectionPage
        hideExportExcel
        tablet
        sectionName="SENDS_ANALYSIS"
        mainBoxWidth="70%"
        stats={stats}
        sideWidget={<SendsSummary stats={stats} />}
        tabs={[statsCampaignName || 'OUTCOMES_ANALYSIS']}
        heightQuery={heightQuery}
        columns={CONTACT_COLUMNS}
        customClick={handleOpenCustomer}
        customStatusChipCallback={({ data }) => (
          <Tooltip
            placement="bottom"
            title={intl.messages.DISPLAY_MESSAGE_CONTENT}
          >
            <Button
              id="display-message-btn"
              variant="outlined"
              disableElevation
              fullWidth
              startIcon={<TextSnippetOutlinedIcon />}
              sx={{ textTransform: 'none' }}
              onClick={(e) => {
                e.stopPropagation(); //prevent row onClick
                setMessageToShow(data);
              }}
            >
              {intl.messages.MESSAGE}
            </Button>
          </Tooltip>
        )}
        ListObserverWrapper={ObserverWrapper}
        filters={FILTERS}
        sortingOptions={SORTING}
        customFilters={
          <FiltersWidget
            tags={tags}
            onConfirm={handleApplyFilters}
            defaultFilters={searchValues}
          />
        }
      />
    </>
  );
};

SendsAnalysisPage.propTypes = {
  heightQuery: PropTypes.bool,
};

export default SendsAnalysisPage;
